import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Button, Badge,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@agion/ui'
import { Copy, Check, RefreshCw, Wifi, WifiOff, AlertCircle } from 'lucide-react'
import {
  HOST_LOG_MAX_RECONNECT_ATTEMPTS,
  canonicalHostLogService,
  useHostLogServices,
  useHostLogs,
} from '@/hooks/infra_use_host_logs'
import type { HostLogLine, HostLogService } from '@/hooks/infra_use_host_logs'

const LEVEL_COLORS: Record<string, string> = {
  error: 'text-red-400',
  warn: 'text-yellow-300',
  info: 'text-slate-300',
  debug: 'text-slate-500',
}

// Force 24h clock for log timestamps regardless of user locale — operator
// debugging relies on a consistent format. Not user-prose, not translated.
const LOG_TIMESTAMP_LOCALE = 'en-GB'
const LOG_TIMESTAMP_OPTIONS: Intl.DateTimeFormatOptions = { hour12: false }

interface Props {
  hostId: string
  hostName?: string
}

/** Per-service log terminal pane with auto-scroll. */
function ServiceLogPane({ logs, connectionState, errorsOnly }: {
  logs: HostLogLine[]
  connectionState: string
  errorsOnly: boolean
}) {
  const { t } = useTranslation(['pages'])
  const logEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)

  const filtered = useMemo(
    () => errorsOnly ? logs.filter(l => l.level === 'error' || l.level === 'warn') : logs,
    [logs, errorsOnly],
  )

  useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [filtered.length, autoScroll])

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 50)
  }, [])

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-slate-950 rounded-lg border border-slate-800 p-3 font-mono text-xs overflow-auto"
        style={{ height: '480px' }}
      >
        {filtered.length === 0 && (
          <div className="text-slate-600 text-center py-8">
            {connectionState === 'streaming'
              ? t('pages:hostLogs.waiting', 'Waiting for log entries...')
              : t('pages:hostLogs.noLogs', 'No logs yet')}
          </div>
        )}
        {filtered.map((log, i) => (
          <div key={i} className="flex gap-2 py-0.5 leading-relaxed">
            <span className="text-slate-600 shrink-0 select-none">
              {new Date(log.timestamp).toLocaleTimeString(LOG_TIMESTAMP_LOCALE, LOG_TIMESTAMP_OPTIONS)}
            </span>
            {log.level !== 'info' && (
              <span className={`shrink-0 font-bold ${LEVEL_COLORS[log.level] || ''}`}>
                {log.level.toUpperCase()}
              </span>
            )}
            <span className={LEVEL_COLORS[log.level] || 'text-slate-300'}>
              {log.message}
            </span>
          </div>
        ))}
        <div ref={logEndRef} />
      </div>
      {/* Scroll-to-bottom pill */}
      {!autoScroll && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setAutoScroll(true)
            logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
          }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-slate-800 text-blue-400 text-xs border border-slate-700 hover:bg-slate-700 transition-colors"
        >
          {t('pages:hostLogs.scrollToBottom', 'Scroll to bottom')}
        </Button>
      )}
    </div>
  )
}

export function HostLogsViewer({ hostId, hostName }: Props) {
  const { t } = useTranslation(['pages'])
  const [searchParams, setSearchParams] = useSearchParams()

  // The tabs are the services this host's agent-host streams.
  const { services, isLoading: servicesLoading } = useHostLogServices(hostId)
  const serviceIds = useMemo(() => services.map(s => s.id), [services])
  const fileServiceIds = useMemo(
    () => services.filter(s => s.source === 'file').map(s => s.id),
    [services],
  )
  const isService = useCallback(
    (service: string | null): service is HostLogService => !!service && serviceIds.includes(service),
    [serviceIds],
  )

  // Derive active tab from URL ?service= param, fallback to first service
  const rawService = searchParams.get('service') ?? ''
  const canonicalService = canonicalHostLogService(rawService)
  const activeTab: HostLogService = isService(canonicalService) ? canonicalService : serviceIds[0] ?? 'agent-host'
  const activeIsJournal = services.some(s => s.id === activeTab && s.source === 'journal')

  // Write fallback to URL once the list is known if ?service= was missing or invalid
  const initializedRef = useRef(false)
  useEffect(() => {
    if (servicesLoading || initializedRef.current) return
    initializedRef.current = true
    if (rawService !== activeTab) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set('service', activeTab)
        return next
      }, { replace: true })
    }
  }, [servicesLoading, rawService, activeTab, setSearchParams])

  // Tab change handler - update URL with replace to avoid history bloat
  const onTabChange = useCallback((value: string) => {
    const canonical = canonicalHostLogService(value)
    if (!isService(canonical)) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('service', canonical)
      return next
    }, { replace: true })
  }, [isService, setSearchParams])
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [copied, setCopied] = useState(false)

  // File services share one stream. A journal service (cloudflared) runs
  // journalctl -f on the host, so it streams only while its tab is open.
  const fileStream = useHostLogs({
    hostId,
    services: fileServiceIds,
    enabled: !servicesLoading,
  })
  const journalStream = useHostLogs({
    hostId,
    services: activeIsJournal ? [activeTab] : [],
    enabled: activeIsJournal,
  })
  const { connectionState, error, errorKind, reconnect, clearLogs } = activeIsJournal ? journalStream : fileStream

  // Split logs by service unit
  const logsByService = useMemo(() => {
    const map = new Map<HostLogService, HostLogLine[]>(serviceIds.map(s => [s, []]))
    for (const log of [...fileStream.logs, ...journalStream.logs]) {
      const unit = log.unit ?? 'agent-host'
      const canonicalUnit = canonicalHostLogService(unit)
      const matched =
        isService(canonicalUnit)
          ? canonicalUnit
          : serviceIds.find(s => unit.startsWith(s)) ?? 'agent-host'
      map.get(matched)?.push(log)
    }
    return map
  }, [fileStream.logs, journalStream.logs, serviceIds, isService])

  const logsFor = useCallback((svc: HostLogService) => logsByService.get(svc) ?? [], [logsByService])

  // Count errors per service for badge indicators
  const errorCounts = useMemo(() => {
    const counts = new Map<HostLogService, number>()
    for (const svc of serviceIds) {
      counts.set(svc, logsFor(svc).filter(l => l.level === 'error').length)
    }
    return counts
  }, [logsFor, serviceIds])

  const activeServiceLogs = logsFor(activeTab)

  const formatForLLM = useCallback((logLines: HostLogLine[]) => {
    const header = `=== Host Logs: ${hostName || hostId} / ${activeTab} (${new Date().toISOString()}) ===`
    const lines = logLines.map(l => {
      const time = new Date(l.timestamp).toLocaleTimeString('en-GB', { hour12: false })
      const level = l.level.toUpperCase().padEnd(5)
      return `[${time}] [${level}] ${l.message}`
    })
    return `${header}\n${lines.join('\n')}`
  }, [hostId, hostName, activeTab])

  const copyForLLM = useCallback(async () => {
    const visible = errorsOnly
      ? activeServiceLogs.filter(l => l.level === 'error' || l.level === 'warn')
      : activeServiceLogs
    await navigator.clipboard.writeText(formatForLLM(visible))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [activeServiceLogs, errorsOnly, formatForLLM])

  return (
    <div className="space-y-3">
      {/* Error banner */}
      {error && (
        <div role="alert" className="px-3 py-2 rounded-md bg-red-500/10 text-red-400 text-xs">
          {errorKind
            ? t(`pages:hostLogs.errors.${errorKind}`, error, { count: HOST_LOG_MAX_RECONNECT_ATTEMPTS })
            : error}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={onTabChange}>
        {/* Tab bar + controls in one row */}
        <div className="flex items-center justify-between gap-3">
          <TabsList className="max-w-full overflow-x-auto">
            {serviceIds.map(svc => (
              <TabsTrigger key={svc} value={svc} className="gap-1.5 text-xs">
                {t(`pages:hostLogs.services.${svc}`, svc)}
                {(errorCounts.get(svc) ?? 0) > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-red-500/20 text-red-400 text-[10px] font-medium">
                    {errorCounts.get(svc)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex items-center gap-1.5 shrink-0">
            {connectionState === 'streaming' && (
              <Badge variant="success" className="gap-1">
                <Wifi className="h-3 w-3" />
                {t('pages:hostLogs.streaming', 'Live')}
              </Badge>
            )}
            {connectionState === 'error' && (
              <Badge variant="error" className="gap-1">
                <WifiOff className="h-3 w-3" />
                {t('pages:hostLogs.disconnected', 'Disconnected')}
              </Badge>
            )}

            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setErrorsOnly(prev => !prev)}
              className={`p-1.5 rounded-md transition-colors ${
                errorsOnly
                  ? 'text-red-400 bg-red-500/10'
                  : 'text-slate-400 hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              title={t('pages:hostLogs.errorsOnly', 'Errors only')}
            >
              <AlertCircle className="h-4 w-4" />
            </Button>

            <Button variant="ghost" size="icon-sm" onClick={reconnect} title={t('pages:hostLogs.reconnect', 'Reconnect')}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void copyForLLM()}
              title={copied ? t('pages:hostLogs.copied', 'Copied!') : t('pages:hostLogs.copyForLLM', 'Copy for LLM')}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>

            <Button variant="ghost" size="icon-sm" onClick={clearLogs} title={t('pages:hostLogs.clear', 'Clear')}>
              <span className="text-xs font-medium">CLR</span>
            </Button>
          </div>
        </div>

        {/* Tab content — each service gets its own log pane */}
        {serviceIds.map(svc => (
          <TabsContent key={svc} value={svc} className="mt-3">
            <ServiceLogPane
              logs={logsFor(svc)}
              connectionState={connectionState}
              errorsOnly={errorsOnly}
            />
            <div className="mt-1.5 text-xs text-slate-500">
              {logsFor(svc).length} {t('pages:hostLogs.lines', 'lines')}
              {errorsOnly && ' '}
              {errorsOnly && t(
                'pages:hostLogs.errorsAndWarningsCount',
                '({{count}} errors/warnings)',
                { count: logsFor(svc).filter(l => l.level === 'error' || l.level === 'warn').length }
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
